import React, {
  Children,
  cloneElement,
  createContext,
  FC,
  isValidElement,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  HTMLAttributes,
  CSSProperties,
  RefObject,
} from 'react';
import { useIsomorphicLayoutEffect, useResizeObserver } from '../../hooks';
import { GridContainer, GridContainerProps } from '../Grid/GridContainer';
import { DotPagination } from '../DotPagination';
import { Dot } from '../DotPagination/Dot';
import styles from './styles.module.css';

interface ScrollContextProps {
  scrollableRef: RefObject<HTMLDivElement>;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollToDot: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
}

interface PaginationContextProps {
  totalDots: number;
  activeDot: number;
  setTotalItems: (count: number) => void;
  setItemsPerDot: (count: number) => void;
}

interface VisualContextProps {
  setFade: (enabled: boolean) => void;
  showLeftFade: boolean;
  showRightFade: boolean;
  setShadowSpace: (space: string | number) => void;
}

interface CarouselContextProps
  extends ScrollContextProps,
    PaginationContextProps,
    VisualContextProps {}

const CarouselContext = createContext<CarouselContextProps | null>(null);

const useCarousel = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel deve ser usado dentro de um <Carousel>');
  }
  return context;
};

interface RootProps extends HTMLAttributes<HTMLDivElement> {
  scrollOnWheel?: boolean;
}

const Root: FC<RootProps> = ({
  children,
  scrollOnWheel = true,
  className,
  style,
  ...rest
}) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [activeDot, setActiveDot] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [itemsPerDot, setItemsPerDot] = useState(1);
  const [fade, setFade] = useState(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(fade);
  const [shadowSpace, setShadowSpace] = useState<string | number>(0);

  const totalDots = itemsPerDot > 0 ? Math.ceil(totalItems / itemsPerDot) : 0;

  const getGapValue = (element: HTMLElement | null) => {
    if (typeof window === 'undefined' || !element) return 0;

    return parseInt(window.getComputedStyle(element).columnGap, 10) || 0;
  };

  const updateUiStates = useCallback(() => {
    const element = scrollableRef.current;
    if (!element) return;

    const atStart = element.scrollLeft <= 1;
    const atEnd =
      element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
    setCanScrollPrev(!atStart);
    setCanScrollNext(!atEnd);

    if (fade) {
      setShowLeftFade(!atStart);
      setShowRightFade(!atEnd);
    }
  }, [fade]);

  const scrollByItem = useCallback((direction: 'left' | 'right') => {
    const element = scrollableRef.current;
    if (!element || element.children.length === 0) return;

    const gap = getGapValue(element);
    const firstItem = element.children[0] as HTMLElement;
    const scrollAmount = firstItem.offsetWidth + gap;

    element.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  const scrollToDot = useCallback(
    (dotIndex: number) => {
      const element = scrollableRef.current;
      if (!element || element.children.length === 0) return;

      const gap = getGapValue(element);
      const firstItem = element.children[0] as HTMLElement;
      const itemWidthWithGap = firstItem.offsetWidth + gap;
      const scrollPosition = dotIndex * itemsPerDot * itemWidthWithGap;

      element.scrollTo({ left: scrollPosition, behavior: 'smooth' });
    },
    [itemsPerDot]
  );

  useIsomorphicLayoutEffect(() => {
    const element = scrollableRef.current;
    if (!element) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      element.scrollLeft += e.deltaY;
    };

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDown = true;
      startX = e.pageX - element.offsetLeft;
      scrollLeft = element.scrollLeft;
    };

    const onMouseLeaveOrUp = () => {
      isDown = false;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;

      e.preventDefault();
      const x = e.pageX - element.offsetLeft;
      const walk = (x - startX) * 2;

      element.scrollLeft = scrollLeft - walk;
    };

    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        updateUiStates();
        if (element.children.length === 0 || totalDots <= 1) return;

        const isAtEnd =
          element.scrollLeft + element.clientWidth >= element.scrollWidth - 5;

        if (isAtEnd) {
          setActiveDot(totalDots - 1);
        } else {
          const gap = getGapValue(element);
          const firstItem = element.children[0] as HTMLElement;
          const itemWidthWithGap = firstItem.offsetWidth + gap;
          const scrollPerDot = itemWidthWithGap * itemsPerDot;

          if (scrollPerDot > 0) {
            setActiveDot(Math.round(element.scrollLeft / scrollPerDot));
          }
        }
      }, 120);
    };

    if (scrollOnWheel) {
      element.addEventListener('wheel', onWheel, { passive: false });
    }
    element.addEventListener('mousedown', onMouseDown);
    element.addEventListener('mouseleave', onMouseLeaveOrUp);
    element.addEventListener('mouseup', onMouseLeaveOrUp);
    element.addEventListener('mousemove', onMouseMove);
    element.addEventListener('scroll', handleScroll, { passive: true });

    const { ResizeObserverImpl } = useResizeObserver();
    const observer = new ResizeObserverImpl(() => {
      updateUiStates();
    });
    observer.observe(element);

    updateUiStates();

    return () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('mousedown', onMouseDown);
      element.removeEventListener('mouseleave', onMouseLeaveOrUp);
      element.removeEventListener('mouseup', onMouseLeaveOrUp);
      element.removeEventListener('mousemove', onMouseMove);
      element.removeEventListener('scroll', handleScroll);
      if (observer && element) observer.unobserve(element);
    };
  }, [updateUiStates, totalDots, itemsPerDot, scrollOnWheel]);

  const contextValue: CarouselContextProps = {
    scrollableRef,
    scrollPrev: () => scrollByItem('left'),
    scrollNext: () => scrollByItem('right'),
    scrollToDot,
    setTotalItems,
    setItemsPerDot,
    setFade,
    totalDots,
    activeDot,
    canScrollPrev,
    canScrollNext,
    showLeftFade,
    showRightFade,
    setShadowSpace,
  };

  const inlineStyle = {
    ...(shadowSpace && {
      '--carousel-shadow-space':
        typeof shadowSpace === 'number' ? `${shadowSpace}px` : shadowSpace,
    }),
  } as CSSProperties;

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        className={[styles.carousel, className].join(' ')}
        style={{ ...style, ...inlineStyle }}
        {...rest}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

const Body = ({
  children,
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <div className={styles.carouselBody}>{children}</div>
);

interface CarouselContentProps extends GridContainerProps {
  fade?: boolean;
  fadeColor?: string;
  overflowOffset?: number | string;
}

const Content: FC<CarouselContentProps> = ({
  children,
  className,
  fade = false,
  fadeColor = 'transparent',
  overflowOffset = 0,
  style,
  ...props
}) => {
  const {
    scrollableRef,
    showLeftFade,
    showRightFade,
    setTotalItems,
    setFade,
    setShadowSpace,
  } = useCarousel();

  useEffect(() => {
    setTotalItems(Children.count(children));
  }, [children, setTotalItems]);

  useEffect(() => {
    setFade(fade);
  }, [fade, setFade]);

  useEffect(() => {
    setShadowSpace(overflowOffset);
  }, [overflowOffset, setShadowSpace]);

  const viewportStyle: CSSProperties = {
    ...(fadeColor && {
      '--carousel-fade-color': fadeColor,
    }),
  } as CSSProperties;

  const containerStyle: CSSProperties = {};
  if (overflowOffset) {
    containerStyle.paddingBottom =
      typeof overflowOffset === 'number'
        ? `${overflowOffset}px`
        : overflowOffset;
  }

  return (
    <div
      style={viewportStyle}
      className={[
        styles.scrollViewport,
        showLeftFade ? styles.showLeft : '',
        showRightFade ? styles.showRight : '',
      ].join(' ')}
    >
      <GridContainer
        ref={scrollableRef}
        className={[styles.carouselContainer, className].join(' ')}
        style={{ ...style, ...containerStyle }}
        {...props}
      >
        {children}
      </GridContainer>
    </div>
  );
};

const Item = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={[className].join(' ')} {...props} />
  )
);
Item.displayName = 'CarouselItem';

const Previous: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  const child = Children.only(children);
  if (!isValidElement(child)) {
    return null;
  }

  return cloneElement(child, {
    onClick: scrollPrev,
    disabled: !canScrollPrev,
  });
};

const Next: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { scrollNext, canScrollNext } = useCarousel();

  const child = Children.only(children);
  if (!isValidElement(child)) {
    return null;
  }

  return cloneElement(child, {
    onClick: scrollNext,
    disabled: !canScrollNext,
  });
};

interface CarouselPaginationProps extends HTMLAttributes<HTMLDivElement> {
  itemsPerDot?: number;
  activeDotColor?: string;
  inactiveDotColor?: string;
  inactiveDotBorderColor?: string;
}

const Pagination: FC<CarouselPaginationProps> = ({
  itemsPerDot = 1,
  activeDotColor,
  inactiveDotColor,
  inactiveDotBorderColor,
  ...props
}) => {
  const { totalDots, activeDot, scrollToDot, setItemsPerDot } = useCarousel();

  useEffect(() => {
    setItemsPerDot(itemsPerDot);
  }, [itemsPerDot, setItemsPerDot]);

  if (totalDots <= 1) return null;

  return (
    <div className={styles.paginationDots} {...props}>
      <DotPagination>
        {Array.from({ length: totalDots }).map((_, index) => (
          <Dot
            key={index}
            active={index === activeDot}
            activeColor={activeDotColor}
            inactiveColor={inactiveDotColor}
            inactiveBorderColor={inactiveDotBorderColor}
            onClick={() => scrollToDot(index)}
            // aria-label={`Ir para o item ${index * itemsPerDot + 1}`}
          />
        ))}
      </DotPagination>
    </div>
  );
};

export const Carousel = Object.assign(Root, {
  Body,
  Content,
  Item,
  Previous,
  Next,
  Pagination,
});
